import { useState } from "react";
import { useAppSettings } from "#/hooks/use-app-settings";
import {
	BUILDER_FEE_PERCENT,
	computeRatios,
	DEFAULT_REVENUE_SHARE_BPS,
	estimateMonthlyRebate,
	REFERRAL_PRODUCTS,
	simulatorVolume,
} from "../lib/config";

/**
 * Orchestrates the logged-out gate: the "up to {max}%" headline ratio plus
 * the earnings-simulator state (slider position → volume → monthly rebate).
 * Renders immediately with `DEFAULT_REVENUE_SHARE_BPS` while app settings
 * load — the gate is a conversion surface, so no loader and no headline
 * swap; the server value silently corrects the numbers when it arrives
 * (usually identical). An explicit server 0 (revshare disabled) still wins
 * over the fallback: fallback headline, simulator hidden.
 */
export function useReferralLoginGate() {
	const settings = useAppSettings();
	const bps =
		settings.data?.default_revenue_share_bps ?? DEFAULT_REVENUE_SHARE_BPS;
	// Your cut as a % of friends' traded notional (builder fee × revenue share).
	const rebateRatePct = BUILDER_FEE_PERCENT * (bps / 10_000);
	const { maxRatio } = computeRatios(rebateRatePct, REFERRAL_PRODUCTS);

	const [sliderValue, setSliderValue] = useState(50);
	const volume = simulatorVolume(sliderValue);

	return {
		maxRounded: Math.floor(maxRatio),
		rebateRatePct,
		rateLabel: Number(rebateRatePct.toFixed(4)).toString(),
		sliderValue,
		setSliderValue,
		volume,
		monthlyRebate: estimateMonthlyRebate(volume, rebateRatePct),
		// Illustrative rebate on one $5k friend trade — rides the diagram chip.
		sampleTradeRebate: estimateMonthlyRebate(5_000, rebateRatePct),
	};
}
